import {
  ATTRIBUTION_SESSION_TIMEOUT_MS,
  ATTRIBUTION_STORAGE_KEYS,
  AttributionConsentState,
  MarketingParams,
} from '@/lib/attribution/types'
import { attributionDebug } from '@/lib/attribution/logger'
import { resolveCookieDomain } from '@/lib/utils'

function isBrowser() {
  return typeof window !== 'undefined'
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0
    const value = char === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

// ---------------------------------------------------------------------------
// Cookie helpers (attribution-specific: SameSite=Lax, URI-encoded values)
// ---------------------------------------------------------------------------

function readCookie(name: string): string | null {
  if (!isBrowser()) return null
  try {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      const raw = parts.pop()?.split(';')?.shift()
      return raw ? decodeURIComponent(raw) : null
    }
    return null
  } catch {
    return null
  }
}

function writeCookie(name: string, value: string, days?: number): void {
  if (!isBrowser()) return
  try {
    const domain = resolveCookieDomain()
    const encodedValue = encodeURIComponent(value)

    let expires = ''
    if (days !== undefined) {
      const date = new Date()
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
      expires = `; expires=${date.toUTCString()}`
    }

    const secure = domain !== 'localhost' ? '; Secure' : ''

    document.cookie = `${name}=${encodedValue}${expires}; path=/; domain=${domain}; SameSite=Lax${secure}`
  } catch {
    // Ignore cookie write errors
  }
}

function removeCookie(name: string): void {
  if (!isBrowser()) return
  try {
    const domain = resolveCookieDomain()
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; domain=${domain}`
  } catch {
    // Ignore cookie removal errors
  }
}

// ---------------------------------------------------------------------------
// JSON parse helper
// ---------------------------------------------------------------------------

function parseJson<T>(value: string | null): T | null {
  if (!value) return null

  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Anonymous ID persistence (cookie-based, TTL via cookie expires)
// ---------------------------------------------------------------------------

function persistAnonymousId(id: string) {
  attributionDebug('Persisting anonymous_id as persistent cookie (390 days)', {
    anonymousId: id,
  })
  writeCookie(ATTRIBUTION_STORAGE_KEYS.anonymousId, id, 390)
}

function writeSessionAnonymousId(id: string) {
  // Session cookie: no days parameter → no expires attribute
  writeCookie(ATTRIBUTION_STORAGE_KEYS.anonymousId, id)
}

// ---------------------------------------------------------------------------
// Referrer / URL helpers
// ---------------------------------------------------------------------------

function isMeaningfulReferrer(referrer: string): boolean {
  if (!isBrowser() || !referrer) return false

  try {
    const referrerUrl = new URL(referrer)
    if (!['http:', 'https:'].includes(referrerUrl.protocol)) return false
    return referrerUrl.host !== window.location.host
  } catch {
    return false
  }
}

function getCurrentUrl(): URL | null {
  if (!isBrowser()) return null

  try {
    return new URL(window.location.href)
  } catch {
    return null
  }
}

function extractMarketingParams(url: URL): MarketingParams {
  const params: MarketingParams = {}
  const searchParams = url.searchParams

  const utmSource = searchParams.get('utm_source')
  const utmMedium = searchParams.get('utm_medium')
  const utmCampaign = searchParams.get('utm_campaign')
  const utmTerm = searchParams.get('utm_term')
  const utmContent = searchParams.get('utm_content')
  const gclid = searchParams.get('gclid')
  const fbclid = searchParams.get('fbclid')

  if (utmSource) params.utm_source = utmSource
  if (utmMedium) params.utm_medium = utmMedium
  if (utmCampaign) params.utm_campaign = utmCampaign
  if (utmTerm) params.utm_term = utmTerm
  if (utmContent) params.utm_content = utmContent
  if (gclid) params.gclid = gclid
  if (fbclid) params.fbclid = fbclid

  return params
}

function hasMarketingParams(params: MarketingParams): boolean {
  return Object.values(params).some(Boolean)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getAttributionConsent(): AttributionConsentState {
  /*const stored = readCookie(ATTRIBUTION_STORAGE_KEYS.consent)
  if (stored === 'granted' || stored === 'denied') {
    return stored
  }
  return 'unknown'*/

  // For now, we treat all users as consenting to attribution, to avoid losing data while we don't have a proper consent management solution in place.
  attributionDebug('Consent check resolved as granted (temporary default)')
  return 'granted'
}

export function setAttributionConsent(granted: boolean): void {
  if (!isBrowser()) return

  const consentState: AttributionConsentState = granted ? 'granted' : 'denied'
  attributionDebug('Setting attribution consent state', {
    consent: consentState,
  })
  writeCookie(ATTRIBUTION_STORAGE_KEYS.consent, consentState, 730)

  if (granted) {
    const existingId = readCookie(ATTRIBUTION_STORAGE_KEYS.anonymousId)
    const anonymousId = existingId ?? generateUuid()
    attributionDebug('Consent granted; upgrading anonymous_id to persistent cookie', {
      existingId,
      resolvedAnonymousId: anonymousId,
    })
    persistAnonymousId(anonymousId)
    return
  }

  const existingId = readCookie(ATTRIBUTION_STORAGE_KEYS.anonymousId)
  if (existingId) {
    attributionDebug('Consent denied; downgrading anonymous_id to session cookie', {
      anonymousId: existingId,
    })
    writeSessionAnonymousId(existingId)
  }
}

export function getAnonymousId(): string {
  if (!isBrowser()) return ''

  const consentState = getAttributionConsent()
  attributionDebug('Resolving anonymous_id', {
    consentState,
  })

  const existingId = readCookie(ATTRIBUTION_STORAGE_KEYS.anonymousId)

  if (consentState === 'granted') {
    if (existingId) {
      attributionDebug('Anonymous_id resolved from persistent cookie', {
        anonymousId: existingId,
      })
      // Re-persist to ensure it has the full 390-day TTL (upgrades session → persistent)
      persistAnonymousId(existingId)
      return existingId
    }

    const anonymousId = generateUuid()
    attributionDebug('No anonymous_id found; creating persistent id', {
      resolvedAnonymousId: anonymousId,
    })
    persistAnonymousId(anonymousId)
    return anonymousId
  }

  if (existingId) {
    attributionDebug('Anonymous_id resolved from session cookie', {
      anonymousId: existingId,
    })
    return existingId
  }

  const anonymousId = generateUuid()
  attributionDebug('Generated new session-scoped anonymous_id', {
    anonymousId,
  })
  writeSessionAnonymousId(anonymousId)
  return anonymousId
}

export function getSessionId(): string {
  if (!isBrowser()) return ''

  const now = Date.now()
  const storedSessionId = readCookie(ATTRIBUTION_STORAGE_KEYS.sessionId)
  const lastActivityRaw = readCookie(ATTRIBUTION_STORAGE_KEYS.lastActivity)
  const lastActivity = lastActivityRaw ? Number(lastActivityRaw) : NaN
  const timedOut = !Number.isFinite(lastActivity) || now - lastActivity > ATTRIBUTION_SESSION_TIMEOUT_MS

  let sessionId = storedSessionId
  if (!sessionId || timedOut) {
    sessionId = generateUuid()
    writeCookie(ATTRIBUTION_STORAGE_KEYS.sessionId, sessionId)
    attributionDebug('Created/rotated session_id', {
      previousSessionId: storedSessionId,
      newSessionId: sessionId,
      timedOut,
      lastActivity,
    })
  } else {
    attributionDebug('Using existing session_id', {
      sessionId,
      lastActivity,
    })
  }

  writeCookie(ATTRIBUTION_STORAGE_KEYS.lastActivity, String(now))
  attributionDebug('Updated last activity timestamp', {
    lastActivity: now,
  })

  return sessionId
}

export function hasFirstTouchMarketingSignals(): boolean {
  const currentUrl = getCurrentUrl()
  if (!currentUrl) return false
  const marketingParams = extractMarketingParams(currentUrl)
  const hasSignals = hasMarketingParams(marketingParams)
  attributionDebug('Evaluated first-touch marketing signals', {
    hasSignals,
    marketingParams,
    pathname: currentUrl.pathname,
  })
  return hasSignals
}

export function getSessionMarketingParams(): MarketingParams {
  if (!isBrowser()) return {}

  const stored = parseJson<MarketingParams>(readCookie(ATTRIBUTION_STORAGE_KEYS.utm)) ?? {}
  const currentUrl = getCurrentUrl()
  if (!currentUrl) return stored

  const currentParams = extractMarketingParams(currentUrl)
  if (!hasMarketingParams(currentParams)) {
    attributionDebug('No new marketing params in URL; reusing session params', {
      storedMarketingParams: stored,
      pathname: currentUrl.pathname,
    })
    return stored
  }

  const merged = {
    ...stored,
    ...currentParams,
  }
  writeCookie(ATTRIBUTION_STORAGE_KEYS.utm, JSON.stringify(merged))
  attributionDebug('Captured/merged marketing params into session cookie', {
    previousMarketingParams: stored,
    currentMarketingParams: currentParams,
    mergedMarketingParams: merged,
  })

  return merged
}

export function getSessionReferrer(): string | undefined {
  const storedReferrer = readCookie(ATTRIBUTION_STORAGE_KEYS.referrer)
  if (storedReferrer) {
    attributionDebug('Using stored session referrer', {
      referrerUrl: storedReferrer,
    })
    return storedReferrer
  }
  if (!isBrowser()) return undefined

  const currentReferrer = document.referrer
  if (!isMeaningfulReferrer(currentReferrer)) {
    attributionDebug('Referrer ignored (not meaningful)', {
      referrerUrl: currentReferrer || undefined,
    })
    return undefined
  }

  writeCookie(ATTRIBUTION_STORAGE_KEYS.referrer, currentReferrer)
  attributionDebug('Captured first meaningful session referrer', {
    referrerUrl: currentReferrer,
  })
  return currentReferrer
}

export function getLandingPage(): string {
  if (!isBrowser()) return ''

  const storedLandingPage = readCookie(ATTRIBUTION_STORAGE_KEYS.landingPage)
  if (storedLandingPage) {
    attributionDebug('Using existing session landing page', {
      landingPage: storedLandingPage,
    })
    return storedLandingPage
  }

  const landingPage = window.location.href
  writeCookie(ATTRIBUTION_STORAGE_KEYS.landingPage, landingPage)
  attributionDebug('Captured first session landing page', {
    landingPage,
  })

  return landingPage
}

export function getCurrentPageUrl(): string {
  if (!isBrowser()) return ''
  const pageUrl = window.location.href
  attributionDebug('Resolved current page URL', {
    pageUrl,
  })
  return pageUrl
}

export function clearSessionAttributionStorage(): void {
  attributionDebug('Clearing attribution session cookies')
  removeCookie(ATTRIBUTION_STORAGE_KEYS.sessionId)
  removeCookie(ATTRIBUTION_STORAGE_KEYS.lastActivity)
  removeCookie(ATTRIBUTION_STORAGE_KEYS.utm)
  removeCookie(ATTRIBUTION_STORAGE_KEYS.referrer)
  removeCookie(ATTRIBUTION_STORAGE_KEYS.landingPage)
}
